import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  CalendarDays,
  ClipboardList,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
} from "lucide-react";
import { useAuth } from "@/features/auth/AuthProvider";
import { useOrganization } from "@/hooks/useOrganization";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SignedAvatarImage } from "@/components/SignedAvatarImage";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { StaffHubLockup } from "@/components/brand/StaffHubLockup";
import { isAdminRole } from "@/types/roles";
import { NotificationsBell } from "@/features/push/NotificationsBell";
import { OfflineIndicator } from "@/features/offline/OfflineIndicator";
import { useDisplayProfile } from "@/features/profile/useDisplayProfile";
import { ThemeToggle } from "@/components/ThemeToggle";

const NAV = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/schedule", label: "Schedule", icon: CalendarDays },
  { to: "/app/hours", label: "Hours", icon: ClipboardList },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { roles, signOut } = useAuth();
  const profile = useDisplayProfile();
  const { organization } = useOrganization();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const adminRole = roles.find(isAdminRole);
  const isAdmin = Boolean(adminRole);
  const displayName = profile.name;
  const roleLabel = adminRole ? adminRole.replace("_", " ") : "Staff";

  // Auto-close the mobile drawer on navigation. Without this the drawer
  // stays open after tapping a nav link, which feels broken on phones.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-border bg-card dark:border-foreground/10">
        <div className="px-5 pt-6 pb-5 flex items-start justify-between gap-2">
          <div className="min-w-0 space-y-2">
            <StaffHubLockup className="h-11 w-auto" />
            {organization?.name && (
              <p className="text-xs text-muted-foreground truncate pl-0.5">
                {organization.name}
              </p>
            )}
            <OfflineIndicator />
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <ThemeToggle />
            <NotificationsBell />
          </div>
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-accent/15 text-foreground font-medium"
                    : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-border space-y-2">
          {isAdmin && (
            <NavLink
              to="/admin"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-colors"
            >
              <LayoutDashboard className="h-4 w-4" />
              Admin console
            </NavLink>
          )}
          <div className="flex items-center gap-3 px-2 pt-2">
            <Avatar className="h-9 w-9">
              {profile.avatarUrl && <SignedAvatarImage src={profile.avatarUrl} alt={displayName} />}
              <AvatarFallback>{profile.initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{displayName || "Signed in"}</p>
              <p className="text-xs text-muted-foreground truncate">
                {adminRole ? adminRole.replace("_", " ") : "Staff"}
              </p>
            </div>
          </div>
          <Button onClick={signOut} variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground">
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar — hamburger on the left, notifications on the right.
            Logo sits between as the brand anchor. Profile/theme/sign-out
            live inside the drawer to keep the bar uncluttered. */}
        <header className="md:hidden safe-top border-b border-border bg-card dark:border-foreground/10">
          <div className="px-4 py-3 flex items-center gap-3">
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Open navigation"
                  className="grid h-10 w-10 -ml-2 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground transition-colors"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-[300px] sm:max-w-[320px] bg-card p-0 flex flex-col gap-0 border-r border-border dark:border-foreground/10"
              >
                {/* iOS safe-area spacer — pushes the drawer head below
                    the notch / status bar. Renders 0px on non-notched
                    devices, ~44px on modern iPhones. */}
                <div className="safe-top shrink-0" />

                {/* Drawer head — brand mark, mirrors the desktop sidebar. */}
                <div className="px-5 pt-6 pb-5 border-b border-border dark:border-foreground/10 space-y-1.5">
                  <StaffHubLockup className="h-11 w-auto" />
                  {organization?.name && (
                    <p className="text-xs text-muted-foreground truncate pl-0.5">
                      {organization.name}
                    </p>
                  )}
                </div>

                {/* Primary nav. Same items as desktop sidebar so the two stay
                    in lockstep — fewer divergence surprises. */}
                <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
                  {NAV.map(({ to, label, icon: Icon, end }) => (
                    <NavLink
                      key={to}
                      to={to}
                      end={end}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                          isActive
                            ? "bg-accent/15 text-foreground font-medium"
                            : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground"
                        }`
                      }
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </NavLink>
                  ))}

                  {isAdmin && (
                    <>
                      <div className="my-3 border-t border-border dark:border-foreground/10" />
                      <NavLink
                        to="/admin"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground transition-colors duration-200"
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        Admin console
                      </NavLink>
                    </>
                  )}
                </nav>

                {/* Drawer foot — profile + theme + sign-out, matches the
                    desktop sidebar footer 1:1. safe-bottom clears the
                    iOS home indicator. */}
                <div className="border-t border-border dark:border-foreground/10 px-3 py-4 space-y-3 safe-bottom">
                  <div className="flex items-center gap-3 px-2">
                    <Avatar className="h-10 w-10">
                      {profile.avatarUrl && (
                        <SignedAvatarImage src={profile.avatarUrl} alt={displayName} />
                      )}
                      <AvatarFallback>{profile.initials}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {displayName || "Signed in"}
                      </p>
                      <p className="text-xs text-muted-foreground capitalize truncate">
                        {roleLabel}
                      </p>
                    </div>
                    <ThemeToggle />
                  </div>
                  <Button
                    onClick={signOut}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 text-muted-foreground"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            <div className="flex-1 min-w-0 flex items-center">
              <StaffHubLockup className="h-6 w-auto" />
            </div>

            <OfflineIndicator className="mr-1" />
            <NotificationsBell />
          </div>
        </header>

        {/* Bottom nav removed — primary nav now lives in the hamburger drawer.
            That means content can extend to the bottom safe area without
            being covered, so pb-24 (which compensated for the fixed bar) is
            gone. Desktop keeps its own pb-10 padding. */}
        <main key={location.pathname} className="flex-1 overflow-y-auto pb-10 safe-bottom">
          <div className="mx-auto w-full max-w-3xl px-5 py-6 md:px-8 md:py-10">{children}</div>
        </main>
      </div>
    </div>
  );
}
